import { t as msg } from "./i18n";
import { openDB } from "idb";
import type { Draft } from "../../../../packages/shared/src/types";
import { restoreSendIntent, type SendIntent } from "./sendIntent";

interface StoredDraft {
  key: string;
  owner: string;
  draft: Draft;
}
const database = openDB("a-promise-card", 2, {
  async upgrade(db, oldVersion, _newVersion, transaction) {
    const scoped = db.createObjectStore("owned-drafts", { keyPath: "key" });
    scoped.createIndex("owner", "owner");
    db.createObjectStore("card-links", { keyPath: "key" });
    db.createObjectStore("unassigned-drafts", { keyPath: "id" });
    if (oldVersion === 1) {
      // Unattributed legacy drafts must never be silently assigned to whoever
      // next uses this browser. Preserve them outside normal application reads.
      for (const draft of (await transaction
        .objectStore("drafts")
        .getAll()) as Draft[]) {
        if (draft.senderUserId) {
          const owner = `account:${draft.senderUserId}`;
          await scoped.put({ key: `${owner}/${draft.id}`, owner, draft });
        } else await transaction.objectStore("unassigned-drafts").put(draft);
      }
      db.deleteObjectStore("drafts");
    }
  },
});
export function draftStore(accountId?: string | null) {
  const owner = accountId ? `account:${accountId}` : "guest";
  const key = (id: string) => `${owner}/${id}`;
  return {
    async clear() {
      const db = await database,
        tx = db.transaction(["owned-drafts", "card-links"], "readwrite");
      const prefix = `${owner}/`,
        range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
      await Promise.all([
        tx.objectStore("owned-drafts").delete(range),
        tx.objectStore("card-links").delete(range),
      ]);
      await tx.done;
    },
    async link(
      id: string,
    ): Promise<
      | {
          url?: string;
          tokenRev?: number;
          pending?: { clientRequestId: string; shareToken: string };
        }
      | undefined
    > {
      return (await database).get("card-links", key(id));
    },
    async saveLink(
      id: string,
      value: {
        url?: string;
        tokenRev?: number;
        pending?: { clientRequestId: string; shareToken: string };
      },
    ) {
      await (await database).put("card-links", { ...value, key: key(id) });
    },
    async get(id: string): Promise<Draft | undefined> {
      return (
        (await (await database).get("owned-drafts", key(id))) as
          | StoredDraft
          | undefined
      )?.draft;
    },
    async put(draft: Draft) {
      if (draft.senderUserId && draft.senderUserId !== accountId)
        throw new Error(msg("This draft is unavailable for this account."));
      await (
        await database
      ).put("owned-drafts", {
        key: key(draft.id),
        owner,
        draft,
      } satisfies StoredDraft);
    },
    async all(): Promise<Draft[]> {
      const records = (await (
        await database
      ).getAllFromIndex("owned-drafts", "owner", owner)) as StoredDraft[];
      return records
        .map((r) => r.draft)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },
    async remove(id: string) {
      const db = await database,
        tx = db.transaction("owned-drafts", "readwrite");
      const record = (await tx.store.get(key(id))) as StoredDraft | undefined;
      if (record?.draft.clientRequestId && !record.draft.sentCardId) {
        await tx.done;
        throw new Error(
          msg(
            "Recover the saved send request before deleting it. The server may already have created this card.",
          ),
        );
      }
      await tx.store.delete(key(id));
      await tx.done;
    },
    async claimGuest(intent: SendIntent): Promise<Draft | undefined> {
      if (!accountId) return;
      const db = await database,
        tx = db.transaction("owned-drafts", "readwrite");
      const guest = (await tx.store.get(`guest/${intent.draftId}`)) as
        | StoredDraft
        | undefined;
      if (
        !guest ||
        !restoreSendIntent(
          JSON.stringify(intent),
          intent.id,
          guest.draft,
          accountId,
        )
      ) {
        await tx.done;
        return;
      }
      await tx.store.put({
        key: key(guest.draft.id),
        owner,
        draft: guest.draft,
      } satisfies StoredDraft);
      await tx.store.delete(guest.key);
      await tx.done;
      return guest.draft;
    },
  };
}
